import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Input, Upload, message, Tag, Space, Card, Typography, Popconfirm, Progress, DatePicker } from 'antd';
import { UploadOutlined, SearchOutlined, PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined, DeleteOutlined, CloudUploadOutlined, SoundOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { hasPerm } from '../utils/perms';
import { useAudioPlayer } from '../components/AudioPlayerContext';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || '';

export default function AiAudio({ user }) {
  const canUpload = user?.role === 'admin' || user?.role === 'super_admin' || hasPerm(user, 'ai_audio_upload');
  const canDownload = user?.role === 'admin' || user?.role === 'super_admin' || hasPerm(user, 'ai_audio_download') || hasPerm(user, 'ai_audio_upload');

  const [audioList, setAudioList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [playingId, setPlayingId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const globalPlayer = useAudioPlayer();

  useEffect(() => { fetchAudio(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  const fetchAudio = async (s, dr) => {
    setLoading(true);
    try {
      const params = { search: s !== undefined ? s : search, page };
      const range = dr !== undefined ? dr : dateRange;
      if (range && range[0] && range[1]) {
        params.from = range[0].format('YYYY-MM-DD');
        params.to = range[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/ai-audio', { params });
      setAudioList(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) { message.error('Failed to load AI audio'); }
    setLoading(false);
  };

  const handleTitleEdit = async (id) => {
    if (!editTitleValue.trim()) { message.warning('Title cannot be empty'); return; }
    try {
      await api.put(`/ai-audio/${id}`, { title: editTitleValue.trim() });
      message.success('Title updated');
      setEditingTitle(null);
      fetchAudio();
    } catch (err) { message.error('Failed to update title'); }
  };

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
    fetchAudio(val);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    setPage(1);
    fetchAudio(undefined, dates);
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) { message.warning('Select at least one audio file'); return; }
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    // No title field — filename becomes the title automatically
    uploadFiles.forEach(f => formData.append('audio', f));

    try {
      const res = await api.post('/ai-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); }
      });
      const d = res.data;
      message.success(`${d.uploaded?.length || 0} file(s) uploaded!`);
      if (d.errors?.length) message.warning(`Errors: ${d.errors.join(', ')}`);
      setUploadModal(false);
      setUploadFiles([]);
      setUploadProgress(0);
      fetchAudio();
    } catch (err) { message.error(err.response?.data?.error || 'Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ai-audio/${id}`);
      message.success('Deleted');
      fetchAudio();
    } catch (err) { message.error('Delete failed'); }
  };

  const isAiPlaying = (id) => globalPlayer.track?.id === `ai-${id}` && globalPlayer.playing;

  const playAudio = (record) => {
    const url = `${API_BASE}/${record.file_path}`;
    globalPlayer.play(`ai-${record.id}`, record.title || record.original_name || 'AI Audio', url);
  };

  const downloadAudio = (record) => {
    const token = localStorage.getItem('token');
    const url = `${API_BASE}/api/ai-audio/download/${record.id}`;
    const a = document.createElement('a');
    a.href = url + '?token=' + token;
    a.download = record.filename;
    a.click();
  };

  const handleBulkDownload = async () => {
    if (!selectedRows.length) { message.warning('Select audio files to download'); return; }
    setBulkDownloading(true);
    const token = localStorage.getItem('token');
    for (const record of selectedRows) {
      const url = `${API_BASE}/api/ai-audio/download/${record.id}?token=${token}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = record.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Small delay between downloads to avoid browser blocking
      await new Promise(r => setTimeout(r, 300));
    }
    message.success(`${selectedRows.length} file(s) downloading!`);
    setBulkDownloading(false);
  };

  const rowSelection = canDownload ? {
    selectedRowKeys: selectedRows.map(r => r.id),
    onChange: (_, rows) => setSelectedRows(rows),
  } : undefined;

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true,
      render: (v, record) => editingTitle === record.id ? (
        <Input size="small" value={editTitleValue}
          onChange={e => setEditTitleValue(e.target.value)}
          onPressEnter={() => handleTitleEdit(record.id)}
          onBlur={() => handleTitleEdit(record.id)}
          autoFocus style={{ width: '100%' }} />
      ) : (
        <Space>
          <Text strong>{v}</Text>
          {canUpload && <EditOutlined style={{ cursor: 'pointer', color: '#1890ff', fontSize: 12 }}
            onClick={() => { setEditingTitle(record.id); setEditTitleValue(v); }} />}
        </Space>
      )
    },
    { title: 'File', dataIndex: 'filename', key: 'filename', ellipsis: true,
      render: (v) => <Tag color="blue">{v}</Tag>
    },
    { title: 'Size', dataIndex: 'file_size', key: 'size', width: 90,
      render: (v) => v ? `${(v / 1048576).toFixed(1)} MB` : '—'
    },
    { title: 'Uploaded By', dataIndex: 'uploader_name', key: 'uploader', width: 130,
      render: (v) => v || '—'
    },
    { title: 'Date', dataIndex: 'created_at', key: 'date', width: 110,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—'
    },
    { title: 'Actions', key: 'actions', width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={isAiPlaying(record.id) ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => playAudio(record)} type={isAiPlaying(record.id) ? 'primary' : 'default'}>
            {isAiPlaying(record.id) ? 'Pause' : 'Play'}
          </Button>
          {canDownload && (
            <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadAudio(record)} />
          )}
          {canUpload && (
            <Popconfirm title="Delete this audio?" onConfirm={() => handleDelete(record.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={3} style={{ margin: 0 }}><SoundOutlined /> AI Audio Library</Title>
        <Space wrap>
          <RangePicker onChange={handleDateChange} value={dateRange} format="DD/MM/YYYY" placeholder={['From Date', 'To Date']} />
          <Input.Search placeholder="Search by title..." allowClear onSearch={handleSearch} style={{ width: 250 }} prefix={<SearchOutlined />} />
          {canDownload && selectedRows.length > 0 && (
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleBulkDownload} loading={bulkDownloading}>
              Download Selected ({selectedRows.length})
            </Button>
          )}
          {canUpload && (
            <Button type="primary" icon={<CloudUploadOutlined />} onClick={() => setUploadModal(true)}>Upload AI Audio</Button>
          )}
        </Space>
      </div>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={audioList}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="middle"
          rowSelection={rowSelection}
          pagination={{ current: page, pageSize: 50, total, onChange: (p) => setPage(p), showTotal: (t) => `Total: ${t} audio files` }}
        />
      </Card>

      <Modal
        title="Upload AI Audio (Bulk)"
        open={uploadModal}
        onCancel={() => { setUploadModal(false); setUploadFiles([]); }}
        onOk={handleUpload}
        okText="Upload"
        confirmLoading={uploading}
      >
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
          Select multiple audio files — each file's name will become its title automatically.
        </Text>
        <Upload
          multiple
          beforeUpload={(file) => { setUploadFiles(prev => [...prev, file]); return false; }}
          onRemove={(file) => setUploadFiles(prev => prev.filter(f => f.uid !== file.uid))}
          fileList={uploadFiles.map(f => ({ uid: f.uid || f.name, name: f.name, status: 'done' }))}
          accept=".mp3,.wav,.flac,.aac,.m4a,.ogg,.wma"
        >
          <Button icon={<UploadOutlined />}>Select Audio Files</Button>
        </Upload>
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          Supported: MP3, WAV, FLAC, AAC, M4A, OGG, WMA — Title = filename
        </Text>
        {uploading && <Progress percent={uploadProgress} style={{ marginTop: 12 }} />}
      </Modal>
    </div>
  );
}
